import {DataStatus} from 'platform/components';
import {Box, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {MAIN_TOPBAR_HEIGHT, queryParams, SectionTab} from '@dms/shared';

import {suffixTestId, RequiredTestIdProps, useQueryState} from 'shared/web';

import {SettingsTemplateHeader} from '../../types/SettingsTemplateHeader';
import {SettingsSection} from '../SettingsSection/SettingsSection';
import {SettingsHeader} from './components/SettingsHeader';
import {SettingsHeaderV2} from './components/SettingsHeaderV2';
import {SettingsNavigation} from './components/SettingsNavigation';
import {SETTINGS_SIDEBAR_WIDTH} from './SETTINGS_SIDEBAR_WIDTH';

interface SettingsTemplateProps extends RequiredTestIdProps {
  header: SettingsTemplateHeader;
  isLoading?: boolean;
  isError?: boolean;
  /** Shown in place of the generic failure text when `isError` is set. */
  errorMessage?: string;
  tabs?: SectionTab[];
  onTabChange?: (tabId: string) => void;
  description?: string;
  children?: ReactNode;
  isCreating?: boolean;
  headerVariant?: 'default' | 'v2';
}

export function SettingsTemplate(props: SettingsTemplateProps) {
  const [tabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const activeTab = props.tabs?.find((tab) => tab.queryId === tabId)?.content;
  const children = activeTab ?? props.tabs?.[0]?.content ?? props.children;

  const SettingsHeaderComponent = props.headerVariant === 'v2' ? SettingsHeaderV2 : SettingsHeader;

  return (
    <HStack height="100%">
      <Box
        position="fixed"
        overflowY="auto"
        width={SETTINGS_SIDEBAR_WIDTH}
        top={MAIN_TOPBAR_HEIGHT}
        bottom={0}
        padding={4}
        borderRight="1px solid"
        borderColor="general.separator"
        backgroundColor="general.white"
      >
        <SettingsNavigation />
      </Box>
      <Box paddingLeft={SETTINGS_SIDEBAR_WIDTH} width="100%" backgroundColor="general.white">
        <DataStatus
          isLoading={props.isLoading}
          isError={props.isError}
          errorMessage={props.errorMessage}
          minHeight={60}
        >
          <VStack height="100%">
            <SettingsHeaderComponent
              title={props.header.title}
              titleAddon={props.header.titleAddon}
              subtitle={props.header.subtitle}
              breadcrumbs={props.header.breadcrumbs}
              actions={props.header.actions}
              tabs={props.tabs}
              onTabChange={props.onTabChange}
              isTitleTeriary={props.isCreating}
              alerts={props.header.alerts}
              data-testid={suffixTestId('detail', props)}
            />
            <Box padding={6} flex={1} position="relative" backgroundColor="general.white">
              <VStack height="100%">
                <Show when={props.description}>
                  <SettingsSection>
                    <Text
                      color="secondary"
                      size="small"
                      data-testid={suffixTestId('detail-description', props)}
                    >
                      {props.description}
                    </Text>
                  </SettingsSection>
                  <Space vertical={6} />
                </Show>
                <Box flex={1}>{children}</Box>
              </VStack>
            </Box>
          </VStack>
        </DataStatus>
      </Box>
    </HStack>
  );
}
